import { Flex, Grid, Skeleton } from '@chakra-ui/react'
import { FormProvider, useForm } from 'BrokerTsLib/src/rhf'
import React, { useEffect } from 'react'

import { PageSection } from '@/components/PageSection'
import { Recap } from '@/components/Recap'
import { RecapSummaryHeader } from '@/components/RecapSummary/RecapSummaryHeader'
import { FormSection } from '@/containers/forms/FormSection'
import { useMeetingRecord } from '@/context/MeetingRecordProvider'
import { usePartner } from '@/context/PartnerContext'
import { useSidebar } from '@/context/SidebarContext'
import type { PageSection as Section } from '@/layouts/components/Sidebar'

import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'
import { Insurer } from 'BrokerTsLib/src/enums'
import { CustomWaypoint } from '../../../../components/CustomWaypoint'
import { Loading } from '../../../../components/Loading'
import { useSelectedOffersAddonContext } from '../../../../context/SelectedOffersAddonProvider'
import { useSelectedQuote } from '../../../../context/SelectedQuoteContext'
import { ExtraInsuranceInformation } from '../extra/StepComplete/forms/ExtraInsuranceInformation'
import CommonMeetingRecordForm from '../Step4/forms/CommonMeetingRecordForm'
import { CreateContractForm } from '../Step4/forms/CreateContractForm'
import EssoxMeetingRecordForm from '../Step4/forms/EssoxMeetingRecordForm'
import KapitolCreateContractForm from '../Step4/forms/KapitolCreateContractForm'
import KapitolMeetingRecordForm from '../Step4/forms/KapitolMeetingRecordForm'
import type { Step4FormValues } from '../Step4/Step4'
import { QuoteInformation } from './forms/QuoteInformation'

export const StepComplete = () => {
  const { app } = useApp()
  const isPublicPoleas = app === Apps.publicPoleas
  const { addonOffers, isLoadingData } = useSelectedOffersAddonContext()
  const { offer, isLoading: isLoadingOffer } = useSelectedQuote()
  const { setPageSections, pageSections } = useSidebar()
  const { currentPartner } = usePartner()

  const { refetch: refetchMR, meetingRecord } = useMeetingRecord()
  useEffect(() => {
    refetchMR().then((data) => methods.reset({ meetingRecord: data.data }))
  }, [])

  const methods = useForm<Step4FormValues>({
    defaultValues: { meetingRecord },
  })

  // registrace sekcí
  useEffect(() => {
    if (!offer) return

    const array: Section[] = []
    let i = 1

    array.push({
      id: 'recapitulation',
      sectionTitle: 'Rekapitulace pojištění',
      index: i++,
      children:
        !offer || isLoadingOffer ? (
          <Loading large={true} />
        ) : (
          <CustomWaypoint
            sectionId="recapitulation"
            cardProps={{ sx: { gap: 16, pb: 16 } }}
          >
            <Recap id="recapitulation" sx={{ gap: 16, pb: 16 }}>
              <QuoteInformation offer={offer} />
            </Recap>
          </CustomWaypoint>
        ),
    })

    // animace nacitani doplnkovych pripojisteni
    if (isLoadingData) {
      array.push({
        id: 'extraInsuranceLoading',
        sectionTitle: 'Doplňková připojištění...',
        isDisabled: true,
        index: i,
        children: (
          <CustomWaypoint
            sectionId="extraInsuranceLoading"
            cardProps={{ sx: { gap: 16, pb: 16 } }}
          >
            <Recap id="extraInsuranceLoading" sx={{ gap: 16, pb: 16 }}>
              <RecapSummaryHeader number={i}>
                <Flex>
                  <Skeleton isLoaded={false}>Doplňková připojištění</Skeleton>
                </Flex>
              </RecapSummaryHeader>
              <Loading />
            </Recap>
          </CustomWaypoint>
        ),
      })
      i++
    }
    // vypis vsech doplnkovych pripojisteni (offers)
    else if (addonOffers && addonOffers.length > 0) {
      addonOffers
        .filter(x => x.valid)
        .sort((a, b) => a.insurerId - b.insurerId)
        .forEach((x) => {
          array.push({
            id: x.offerId,
            sectionTitle: x.name,
            index: i,
            children: (
              <CustomWaypoint
                key={x.offerId}
                sectionId={x.offerId}
                cardProps={{ sx: { gap: 16, pb: 16 } }}
              >
                <Recap id={x.offerId.toString()} sx={{ gap: 16, pb: 16 }}>
                  <ExtraInsuranceInformation
                    key={x.offerId}
                    offer={x}
                    sectionTitle={x.name ?? x.riskVariantName}
                    sectionNumber={i}
                  />
                </Recap>
              </CustomWaypoint>
            ),
          })
          i++
        })
    }

    let partnerMeetingRecordForm = null
    let partnerArrangeContractForm = null
    switch (currentPartner!.partner) {
      case 'Kapitol':
        partnerMeetingRecordForm = <KapitolMeetingRecordForm />
        partnerArrangeContractForm = <KapitolCreateContractForm />
        break
      case 'Essox':
      case 'AutoEsa':
      case 'DirectAuto':
        partnerMeetingRecordForm = <EssoxMeetingRecordForm />
        partnerArrangeContractForm = <CreateContractForm />
        break
      default:
        partnerMeetingRecordForm = <CommonMeetingRecordForm />
        partnerArrangeContractForm = <CreateContractForm />
        break
    }

    if (offer.insurerId == Insurer.Kooperativa &&
      (currentPartner!.partner == 'Davocar' ||
        currentPartner!.partner == 'AndyAuto' ||
        currentPartner!.partner == 'TUkas')) {
      partnerMeetingRecordForm = <KapitolMeetingRecordForm />
    }

    if (app != Apps.leasing && !isPublicPoleas)
      array.push({
        id: 'meetingRecord',
        sectionTitle: 'Záznam z jednání',
        index: i,
        children: (
          <CustomWaypoint sectionId="recapitulation">
            <PageSection id={'meetingRecord'}>
              <FormSection
                number={i++}
                sectionTitle={'Záznam z jednání'}
                wide={false}
              >
                {partnerMeetingRecordForm}
              </FormSection>
            </PageSection>
          </CustomWaypoint>
        ),
      })

    // sekce Sjednání
    if (meetingRecord)
      array.push({
        id: 'contract',
        sectionTitle: 'Sjednání',
        index: i,
        useFormSection: true,
        children: (
          <CustomWaypoint sectionId="contract">
            <PageSection id={'contract'}>
              <FormSection number={i++} sectionTitle={'Sjednání'} wide={false}>
                <Grid rowGap={20}>{partnerArrangeContractForm}</Grid>
              </FormSection>
            </PageSection>
          </CustomWaypoint>
        ),
      })

    setPageSections(array)
  }, [offer, isLoadingOffer, isLoadingData, addonOffers, meetingRecord])

  return (
    <FormProvider {...methods}>
      <Grid sx={{ gap: 8 }}>
        {pageSections &&
          pageSections.map((x) => (
            <React.Fragment key={x.id}>{x.children}</React.Fragment>
          ))}
      </Grid>
    </FormProvider>
  )
}
